import { supabase } from './supabase'

export interface PenaltySubmissionData {
  locationId: number
  raceNumber: string
  penaltyId: number
  penaltyTime: number
  judgeEmail: string
  numPenalties?: number
  photoUrl?: string
  eventid: number
  raceoffroleid: number
  notes?: string
}

export interface ApiResponse<T = any> {
  success: boolean
  message?: string
  data?: T
  error?: string
}

export interface UserRoleResponse {
  rolename: string
  roleid: number
  email: string
  judgestation: number | null
}

export interface CreateRaceOfficialData {
  email: string
  firstname?: string
  lastname?: string
  judgestation?: number
}

export interface CreateRaceOfficialResponse {
  officialid: number
  officialuuid: string
  email: string
  firstname: string | null
  lastname: string | null
  roleid: number
  phonenumber: number
  isactive: boolean
  judgestation: number | null
  createdat: string
  createdby: string
}

export interface UserEventResponse {
  currenteventid: number | null
  email: string
}

export const submitPenalty = async (data: PenaltySubmissionData): Promise<ApiResponse> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-penalty`
    
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || 'Failed to submit penalty')
    }

    return result
  } catch (error) {
    console.error('Error submitting penalty:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

/** Digit-prefixed location names first, then alphabetical with numeric-aware compare. */
function sortLocationsByDisplayOrder<T extends { locationname: string | null }>(locations: T[]): T[] {
  return [...locations].sort((a, b) => {
    const an = (a.locationname ?? '').trim()
    const bn = (b.locationname ?? '').trim()
    const ad = /^\d/.test(an)
    const bd = /^\d/.test(bn)
    if (ad !== bd) return ad ? -1 : 1
    return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' })
  })
}

// Helper function to load locations from database
export const loadLocations = async () => {
  try {
    const { data: locations, error } = await supabase
      .from('location')
      .select('locationid, locationname, penaltytime')
      .order('locationid')

    if (error) {
      console.error('Error fetching locations:', error)
      return {
        success: false,
        error: 'Failed to fetch locations'
      }
    }

    return {
      success: true,
      data: sortLocationsByDisplayOrder(locations ?? [])
    }
  } catch (error) {
    console.error('Error loading locations:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to load locations' 
    }
  }
}

// Function to get user role from the edge function
export const getUserRole = async (email: string): Promise<ApiResponse<UserRoleResponse>> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-role`
    
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch user role')
    }

    return {
      success: true,
      data: result
    }
  } catch (error) {
    console.error('Error fetching user role:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Function to create a new race official
export const createRaceOfficial = async (data: CreateRaceOfficialData): Promise<ApiResponse<CreateRaceOfficialResponse>> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-race-official`
    
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    })

    const result = await response.json()

    if (!response.ok) {
      throw new Error(result.error || 'Failed to create race official')
    }

    return {
      success: true,
      data: result.data
    }
  } catch (error) {
    console.error('Error creating race official:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Function to get user's current event ID
export const getUserCurrentEventId = async (email: string): Promise<ApiResponse<UserEventResponse>> => {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-currenteventid`
    
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    })

    const result = await response.json()

    if (!response.ok) {
      // If user not found (404), return success with null currenteventid
      if (response.status === 404) {
        return {
          success: true,
          data: {
            currenteventid: null,
            email: email
          }
        }
      }
      throw new Error(result.error || 'Failed to fetch user current event ID')
    }

    return {
      success: true,
      data: result.data
    }
  } catch (error) {
    console.error('Error getting user current event ID:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for event data
export interface HyRaceEvent {
  eventid: number;
  eventname: string;
  eventdate?: string | null;
  eventstarttime: string;
  eventendtime: string;
  eventlocation: string;
  eventstatus: string;
  createdat: string;
  createdby: string;
  updatedat?: string;
  updatedby?: string;
}

// Interface for event data by region
export interface EventByRegion {
  eventid: number;
  eventname: string;
  eventdate: string | null;
}

export interface EventsListResponse {
  success: boolean;
  events: HyRaceEvent[];
  count: number;
  error?: string;
}

// Penalty type from get-penalty-types edge function
export interface PenaltyType {
  penaltyid: number;
  penaltyname: string;
  penaltytime: number;
}

export interface PenaltyTypesResponse {
  success: boolean;
  data: PenaltyType[];
  error?: string;
}

export async function getPenaltyTypes(): Promise<PenaltyTypesResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-penalty-types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting penalty types:', error);
    return {
      success: false,
      data: [],
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// Function to get events list
export async function getEventsList(): Promise<EventsListResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-events-list`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting events list:', error)
    return {
      success: false,
      events: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for events by region response
export interface EventsByRegionResponse {
  success: boolean;
  events: EventByRegion[];
  count: number;
  error?: string;
}

// Interface for judge set data
export interface JudgeSet {
  judgesetnum: number;
  judgesetdesc: string;
}

// Interface for judge sets by region response
export interface JudgeSetsByRegionResponse {
  success: boolean;
  judgeSets: JudgeSet[];
  count: number;
  error?: string;
}

// Function to get events by region
export async function getEventsByRegion(eventregion: string): Promise<EventsByRegionResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-events-by-region?eventregion=${encodeURIComponent(eventregion)}`
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting events by region:', error)
    return {
      success: false,
      events: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Function to get judge sets by region
export async function getJudgeSetsByRegion(eventregion: string): Promise<JudgeSetsByRegionResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-judgeset-by-eventregion?eventregion=${encodeURIComponent(eventregion)}`
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting judge sets by region:', error)
    return {
      success: false,
      judgeSets: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for event with judge set data
export interface EventWithJudgeSet {
  eventid: number;
  eventname: string;
  eventdate: string | null;
  judgesetnum: number | null;
  judgesetdesc: string | null;
  timingone: string | null;
  timingtwo: string | null;
  islive: boolean;
}

// Interface for events with judge sets response
export interface EventsWithJudgeSetsResponse {
  success: boolean;
  events: EventWithJudgeSet[];
  count: number;
  error?: string;
}

// Function to get events with judge sets by region
export async function getEventsWithJudgeSetsByRegion(eventregion: string): Promise<EventsWithJudgeSetsResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-judgeset-per-regionevents?eventregion=${encodeURIComponent(eventregion)}`
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting events with judge sets by region:', error)
    return {
      success: false,
      events: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for event with penalty metrics
export interface EventWithPenaltyMetrics {
  eventid: number;
  eventname: string;
  eventdate: string | null;
  eventkeycode: string | null;
  islive: boolean;
  totalpenalties: number;
  totalwarnings: number;
  totalapproved: number;
  activejudgelist: string[];
}

export interface EventsWithPenaltyMetricsResponse {
  success: boolean;
  events: EventWithPenaltyMetrics[];
  count: number;
  error?: string;
}

// Function to get events with penalty metrics by region
export async function getPenaltyMetricsByRegion(eventregion: string, datethreshold?: string): Promise<EventsWithPenaltyMetricsResponse> {
  try {
    let apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-penaltymetrics-by-region?eventregion=${encodeURIComponent(eventregion)}`
    
    // Add datethreshold parameter only if provided
    if (datethreshold) {
      apiUrl += `&datethreshold=${encodeURIComponent(datethreshold)}`
    }
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting penalty metrics by region:', error)
    return {
      success: false,
      events: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for race directors response
export interface RaceDirectorsResponse {
  success: boolean;
  raceDirectors: Array<{ email: string }>;
  emails: string[];
  count: number;
  error?: string;
}

// Function to get race directors
export async function getRaceDirectors(): Promise<RaceDirectorsResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-timing-officials`
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting race directors:', error)
    return {
      success: false,
      raceDirectors: [],
      emails: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for create users from race official response
export interface CreateUsersFromRaceOfficialResponse {
  success: boolean;
  message?: string;
  created: number;
  skipped: number;
  failed: number;
  errors?: Array<{ email: string; error: string }>;
  error?: string;
}

export interface CreateHjFromCsvRow {
  email: string;
  displayname: string;
  roleid: number;
}

export interface CreateHjFromCsvResult {
  email: string;
  displayname: string;
  authStatus: 'success' | 'fail';
  authError?: string;
  raceOfficialStatus: 'success' | 'fail';
  raceOfficialError?: string;
}

export interface CreateHjFromCsvResponse {
  success: boolean;
  message?: string;
  results?: CreateHjFromCsvResult[];
  summary?: { created: number; failed: number; skipped: number };
  error?: string;
}

// Function to create users from race officials
export async function createUsersFromRaceOfficial(): Promise<CreateUsersFromRaceOfficialResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-users-from-raceofficial`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating users from race officials:', error)
    return {
      success: false,
      created: 0,
      skipped: 0,
      failed: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

/** Create Auth users + raceofficial rows from CSV-parsed rows */
export async function createHjFromCsv(
  rows: CreateHjFromCsvRow[]
): Promise<CreateHjFromCsvResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-hj-from-csv`

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ rows }),
    })

    const data = await response.json()

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP error! status: ${response.status}`,
        results: data.results,
        summary: data.summary,
      }
    }

    return data
  } catch (error) {
    console.error('Error creating HJs from CSV:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    }
  }
}

// Interface for race official credentials response
export interface RaceOfficialCredential {
  email: string;
  password: string;
}

export interface RaceOfficialCredsResponse {
  success: boolean;
  credentials: RaceOfficialCredential[];
  count: number;
  error?: string;
}

// Function to get race official credentials by judgesetnum
export async function getRaceOfficialCreds(judgesetnum: number): Promise<RaceOfficialCredsResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-raceofficial-creds?judgesetnum=${judgesetnum}`
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting race official credentials:', error)
    return {
      success: false,
      credentials: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for event active status
export interface EventActiveStatus {
  eventid: number;
  islive: boolean;
  judgesetnum: number | null;
}

export interface SaveActiveStatusRequest {
  events: EventActiveStatus[];
  userEmail: string;
}

export interface SaveActiveStatusResponse {
  success: boolean;
  message?: string;
  updated?: number;
  error?: string;
  failedUpdates?: any[];
}

// Function to save judge set event active status
export async function saveJudgesetEventActiveStatus(events: EventActiveStatus[], userEmail: string): Promise<SaveActiveStatusResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-judgeset-event-activestatus`
    
    const requestBody: SaveActiveStatusRequest = {
      events: events,
      userEmail: userEmail
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving judge set event active status:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for save mappings request
export interface SaveHjSetMapping {
  eventid: number;
  judgesetnum: number;
  timingone?: string | null;
  timingtwo?: string | null;
}

export interface SaveHjSetForEventRequest {
  mappings: SaveHjSetMapping[];
  userEmail: string;
}

export interface SaveHjSetForEventResponse {
  success: boolean;
  message?: string;
  mappings?: any[];
  count?: number;
  error?: string;
  failedUpdates?: any[];
}

// Function to save judge set mappings for events
export async function saveHjSetForEvent(mappings: SaveHjSetMapping[], userEmail: string): Promise<SaveHjSetForEventResponse> {
  try {
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-hjset-for-event`
    
    const requestBody: SaveHjSetForEventRequest = {
      mappings: mappings,
      userEmail: userEmail
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving judge set mappings:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for penalty data from hyroxportsheet
export interface HyroxPortSheetData {
  penlogid: number;
  racenumber: string;
  station: string;
  penaltytype: string;
  penaltytime: number;
  status: string;
  eventid: number;
  eventname: string;
  judgename: string;
  timingprocessed: boolean;
  comments: string | null;
  timingcomment: string | null;
  approved_at: string;
  hasimage: boolean;
  record_source: string | null;
  event_key_code: string | null;
  triggered_by: string | null;
  createdate: string | null;
  race_status: string | null;
}

export interface HyroxPortSheetResponse {
  success: boolean;
  penaltyData: HyroxPortSheetData[];
  count: number;
  error?: string;
}

// Interface for penalty image response
export interface PenaltyImageResponse {
  success: boolean;
  athracephotourl: string | null;
  hasImage: boolean;
  error?: string;
}

// Function to get penalty data from hyroxportsheet
export async function getHyroxPortSheet(eventid: number): Promise<HyroxPortSheetResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-hyroxport-sheet?eventid=${eventid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting penalty data:', error)
    return {
      success: false,
      penaltyData: [],
      count: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Function to get penalty image by penlogid
export async function getPenaltyImage(penlogid: number): Promise<PenaltyImageResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-penalty-image?penlogid=${penlogid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting penalty image:', error)
    return {
      success: false,
      athracephotourl: null,
      hasImage: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Interface for updating hyroxport sheet records
export interface UpdateHyroxPortSheetRequest {
  penlogid: number;
  status?: string;
  timingprocessed?: boolean;
  timingcomment?: string;
}

export interface UpdateHyroxPortSheetResponse {
  success: boolean;
  message?: string;
  data?: {
    penlogid: number;
    status: string;
    timingprocessed: boolean;
    timingcomment: string | null;
  };
  error?: string;
}

// Function to update hyroxport sheet record
export async function updateHyroxPortSheet(updateData: UpdateHyroxPortSheetRequest): Promise<UpdateHyroxPortSheetResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-hyroxport-sheet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating hyroxport sheet record:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}
